import { Button, Checkbox, Drawer, Input, Select, Text, toast } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Section, Thumb } from "@/components/ui"
import { useStoreContext } from "@/lib/catalog"
import { formatDate, formatMoney } from "@/lib/format"
import { errorMessage, sdk } from "@/lib/sdk"

/**
 * Shopify's returns on Medusa returns: "Return items" opens a return for the
 * shipped items the customer is sending back (with a reason); "Mark as
 * received" restocks them at the store location. The refund is then issued
 * with the order's Refund button, like Shopify's "Refund" after a return.
 */
const REASONS = ["Unwanted", "Damaged or defective", "Not as described", "Wrong item sent", "Arrived too late", "Other"]

const useReturns = (orderId: string) =>
  useQuery({
    queryKey: ["order-returns", orderId],
    queryFn: () => sdk.client.fetch<{ returns: any[] }>("/admin/returns", { query: { order_id: orderId, fields: "id,status,created_at,received_at,requested_at,canceled_at,location_id,*items,*items.item", limit: 50 } as any }).then((r) => r.returns),
  })

export function ReturnsCard({ order, canWrite }: { order: any; canWrite: boolean }) {
  const { data: returns } = useReturns(order.id)
  const [creating, setCreating] = useState(false)
  const qc = useQueryClient()
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["order-returns", order.id] })
    qc.invalidateQueries({ queryKey: ["order", order.id] })
    qc.invalidateQueries({ queryKey: ["orders"] })
  }
  const receive = useMutation({
    mutationFn: async (r: any) => {
      await sdk.client.fetch(`/admin/returns/${r.id}/receive`, { method: "POST", body: {} })
      await sdk.client.fetch(`/admin/returns/${r.id}/receive-items`, { method: "POST", body: { items: (r.items ?? []).map((i: any) => ({ id: i.item_id, quantity: Number(i.quantity) })) } })
      await sdk.client.fetch(`/admin/returns/${r.id}/receive/confirm`, { method: "POST", body: {} })
    },
    onSuccess: () => (toast.success("Return received and restocked. Issue the refund with Refund."), refresh()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const cancel = useMutation({
    mutationFn: (r: any) => sdk.client.fetch(`/admin/returns/${r.id}/cancel`, { method: "POST", body: {} }),
    onSuccess: () => (toast.success("Return canceled"), refresh()),
    onError: (e) => toast.error(errorMessage(e)),
  })

  const shipped = (order.items ?? []).filter((i: any) => Number(i.detail?.fulfilled_quantity ?? 0) - Number(i.detail?.return_requested_quantity ?? 0) - Number(i.detail?.return_received_quantity ?? 0) > 0)
  const visible = (returns ?? []).filter((r) => r.status !== "canceled" && r.status !== "open")
  if (!visible.length && !(canWrite && shipped.length)) return null
  return (
    <Section
      title="Returns"
      actions={canWrite && shipped.length > 0 && <Button size="small" variant="secondary" onClick={() => setCreating(true)}>Return items</Button>}
    >
      {!visible.length ? (
        <Text size="small" className="px-4 py-3 text-ui-fg-subtle">No returns. Use Return items when a customer sends something back.</Text>
      ) : (
        <ul className="divide-y">
          {visible.map((r) => {
            const received = r.status === "received" || r.status === "partially_received"
            return (
              <li key={r.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-[13px] font-medium">
                    <span className="tf-pill" style={received ? { background: "#e3e3e3", color: "#303030" } : { background: "#fff1b8", color: "#5e4200" }}>{received ? "Returned" : "Return in progress"}</span>
                    <span className="text-ui-fg-subtle">{formatDate(r.received_at ?? r.requested_at ?? r.created_at)}</span>
                  </span>
                  {canWrite && !received && (
                    <span className="flex gap-2">
                      <Button size="small" variant="transparent" isLoading={cancel.isPending} onClick={() => cancel.mutate(r)}>Cancel return</Button>
                      <Button size="small" isLoading={receive.isPending} onClick={() => receive.mutate(r)}>Mark as received</Button>
                    </span>
                  )}
                </div>
                <ul className="flex flex-col gap-1">
                  {(r.items ?? []).map((ri: any) => (
                    <li key={ri.id} className="flex items-center gap-2 text-[13px]">
                      <Thumb src={ri.item?.thumbnail} size={28} />
                      <span className="min-w-0 flex-1 truncate">{ri.item?.product_title ?? ri.item?.title ?? "Item"}{ri.item?.variant_title && ri.item.variant_title !== "Default Title" ? ` · ${ri.item.variant_title}` : ""}</span>
                      <span className="text-ui-fg-subtle">× {ri.quantity}</span>
                      {ri.note && <span className="hidden text-[12px] text-ui-fg-muted sm:inline">({ri.note})</span>}
                    </li>
                  ))}
                </ul>
              </li>
            )
          })}
        </ul>
      )}
      {creating && <CreateReturn order={order} items={shipped} onClose={() => setCreating(false)} onDone={refresh} />}
    </Section>
  )
}

function CreateReturn({ order, items, onClose, onDone }: { order: any; items: any[]; onClose: () => void; onDone: () => void }) {
  const ctx = useStoreContext().data
  const max = (i: any) => Number(i.detail?.fulfilled_quantity ?? 0) - Number(i.detail?.return_requested_quantity ?? 0) - Number(i.detail?.return_received_quantity ?? 0)
  const [pick, setPick] = useState<Record<string, number>>({})
  const [reason, setReason] = useState(REASONS[0])
  const [notify, setNotify] = useState(false)
  const chosen = Object.entries(pick).filter(([, q]) => q > 0)
  const value = chosen.reduce((s, [id, q]) => s + Number(items.find((i) => i.id === id)?.unit_price ?? 0) * q, 0)
  const create = useMutation({
    mutationFn: async () => {
      const { return: ret } = await sdk.client.fetch<{ return: any }>("/admin/returns", { method: "POST", body: { order_id: order.id, location_id: ctx?.locationId, description: reason, no_notification: !notify } })
      try {
        await sdk.client.fetch(`/admin/returns/${ret.id}/request-items`, { method: "POST", body: { items: chosen.map(([id, quantity]) => ({ id, quantity, description: reason })) } })
        await sdk.client.fetch(`/admin/returns/${ret.id}/request`, { method: "POST", body: { no_notification: !notify } })
      } catch (e) {
        await sdk.client.fetch(`/admin/returns/${ret.id}/request`, { method: "DELETE" }).catch(() => undefined)
        throw e
      }
    },
    onSuccess: () => (toast.success("Return created"), onDone(), onClose()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Return items</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-4 overflow-y-auto">
          <Text size="small" className="text-ui-fg-subtle">Choose what the customer is sending back. Items are restocked when you mark the return as received.</Text>
          <ul className="divide-y rounded-lg border">
            {items.map((i) => (
              <li key={i.id} className="flex items-center gap-3 px-3 py-2">
                <Thumb src={i.thumbnail} size={32} />
                <div className="min-w-0 flex-1">
                  <Text size="small" weight="plus" className="truncate">{i.product_title ?? i.title}</Text>
                  <Text size="xsmall" className="text-ui-fg-subtle">{formatMoney(i.unit_price, order.currency_code)} · {max(i)} returnable</Text>
                </div>
                <Input
                  aria-label={`Quantity of ${i.title} to return`}
                  type="number"
                  min={0}
                  max={max(i)}
                  className="w-20"
                  value={pick[i.id] ?? 0}
                  onChange={(e) => setPick({ ...pick, [i.id]: Math.max(0, Math.min(max(i), Math.floor(Number(e.target.value) || 0))) })}
                />
              </li>
            ))}
          </ul>
          <label className="flex flex-col gap-1 text-[13px]">
            Reason
            <Select value={reason} onValueChange={setReason}>
              <Select.Trigger><Select.Value /></Select.Trigger>
              <Select.Content>{REASONS.map((r) => <Select.Item key={r} value={r}>{r}</Select.Item>)}</Select.Content>
            </Select>
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox checked={notify} onCheckedChange={(c) => setNotify(c === true)} /> Email the customer about this return
          </label>
          <div className="rounded-md bg-ui-bg-subtle p-3 text-[13px]">
            Items value: <strong>{formatMoney(value, order.currency_code)}</strong>. Refund it with the order's Refund button after the items arrive.
          </div>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!chosen.length} isLoading={create.isPending} onClick={() => create.mutate()}>Create return</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}
